import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ClockLoader from 'shared/components/ClockLoader';
import SearchableSelect from 'shared/components/SearchableSelect';
import { toast } from 'react-hot-toast';
import { assignTenantService } from 'shared/api/services/tenantAssignmentsApi';
import { useAuth } from 'shared/hooks/useAuth';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';

const AssignServiceToTenantDialog = ({
  open,
  onClose,
  tenantId,
  catalogServices,
  assignedServiceIds,
  onSuccess,
}) => {
  const [serviceId, setServiceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { refreshSessionProfile } = useAuth();

  useEffect(() => {
    if (!open) {
      setServiceId('');
      setSubmitting(false);
    }
  }, [open]);

  const options = useMemo(() => {
    const assigned = new Set(
      (assignedServiceIds || []).map((id) => (id != null ? String(id) : ''))
    );
    return (catalogServices || []).filter((s) => {
      if (!s?.id) return false;
      if (assigned.has(String(s.id))) return false;
      const st = typeof s.status === 'string' ? s.status.toUpperCase() : '';
      if (st === 'DELETED' || st === 'DISABLED') return false;
      return true;
    });
  }, [catalogServices, assignedServiceIds]);

  const handleAssign = async () => {
    if (!tenantId || !serviceId) {
      toast.error('Choose a service');
      return;
    }
    setSubmitting(true);
    try {
      await assignTenantService(tenantId, serviceId);
      const sessionTenantId = getRegistryTenantIdFromSession();
      if (sessionTenantId && String(sessionTenantId) === String(tenantId)) {
        await refreshSessionProfile();
      }
      toast.success('Service assigned to tenant');
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to assign service';
      toast.error(typeof err === 'string' ? err : 'Failed to assign service');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Assign service to tenant</DialogTitle>
      <DialogContent sx={{ pt: 2, overflow: 'visible' }}>
        <SearchableSelect
          label="Service"
          placeholder=""
          size="small"
          value={serviceId}
          onChange={setServiceId}
          disabled={options.length === 0}
          options={options.map((s) => ({
            value: String(s.id),
            label: s.serviceCode
              ? `${s.serviceName ?? s.serviceCode} (${s.serviceCode})`
              : String(s.serviceName ?? s.id),
          }))}
        />
        {options.length === 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="warning.main">
              No services available to assign (add services in Services, or all are already assigned).
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAssign}
          disabled={submitting || !serviceId || options.length === 0}
        >
          {submitting ? <ClockLoader inline size={22} /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignServiceToTenantDialog;
